// (c) chaehongee. Confidential and proprietary.

import type { ReactElement, RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useReadOnlyMode } from '../../store/hooks/useReadOnlyMode'
import { selectLorasState } from '../../store/slices/lorasSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks/hooks'
import { createCanvasStateService } from './services/CanvasStateService'

import styles from './Canvas.module.css'
import { CanvasToolbar, useToolMode } from './features/toolbar'
import { useContextMenu, useContextMenuItems, useDownloadUrl, ContextMenu } from './features/contextMenu'
import { usePromptedPreview, PreviewOverlay, useMarqueeSelection, MarqueeOverlay } from './features/selection'
import { useHiddenItems } from './hooks/useHiddenItems'
import { useUndoRedoShortcuts } from './hooks/useUndoRedoShortcuts'
import { useCanvasModes } from './hooks/useCanvasModes'
import { useCanvasCoordinator } from './hooks/useCanvasCoordinator'
import { useCanvasHandlers } from './hooks/useCanvasHandlers'
import { usePointerDelegates } from './hooks/usePointerDelegates'
import { useBulkActions } from './hooks/useBulkActions'
import { useSessionCleanup } from './hooks/useSessionCleanup'
import useVerticalTreeLayout from './hooks/useVerticalTreeLayout'
import { useFocusOnNode } from './hooks/useFocusOnNode'
import { useSetMaskActions } from './MaskContext'
import { useLoraBrushMode, MaskStage } from './features/brush'
import { CanvasViewport, useCanvasViewstate } from './features/viewport'
import { useCanvasItems, useAutoAddCanvasItems, useAutoAddSelectedNode, usePositionPersistence, ImageItem, VideoItem, useImageResizeCommit } from './features/items'
import { setNodes as setSelectedNodes, clearPendingInitialSelection } from '../../store/slices/selectionSlice'
import { checkSavedNodes, saveNode, unsaveNode } from '../../store/slices/savedNodesSlice'
import { useImageSizes } from './features/items/useImageSizes'
import { useCanvasDrop, DropOverlay } from './features/upload'
import { useFraming, useExportFramedImageWithUpload, FramedImageList, type DrawOverlayHandle } from './features/framing'
import { useCanvasInternalDrag, GhostImageOverlay, ReturningImageOverlay } from './features/composition'
import { selectActiveSession, selectActiveSessionId } from '../../store/selectors/sessionSelectors'
import { selectActiveCanvasItems } from '../../store/selectors/canvasSelectors'
import {
    selectActiveBrushEnabled,
    selectActiveHoveredNodeId,
    selectActivePendingInitialSelectionId,
    selectActivePreviewImageUrl,
    selectActiveSelectedNodeIds,
} from '../../store/selectors/selectionSelectors'

export function Canvas(): ReactElement {
    // Redux State & Dispatch
    const dispatch = useAppDispatch()
    const stateService = useMemo(() => createCanvasStateService(dispatch), [dispatch])
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const isSessionLoading = useAppSelector((s) => (
        activeSessionId ? Boolean(s.session.isLoadingBySessionId[activeSessionId]) : false
    ))
    const selected = useAppSelector(selectActiveSelectedNodeIds)
    const pendingInitialSelectionId = useAppSelector(selectActivePendingInitialSelectionId)
    const hovered = useAppSelector(selectActiveHoveredNodeId)
    const previewUrl = useAppSelector(selectActivePreviewImageUrl)
    const canvasItemsState = useAppSelector(selectActiveCanvasItems)
    const loras = useAppSelector(selectLorasState)
    const globalBrushEnabled = useAppSelector(selectActiveBrushEnabled)
    const readOnly = useReadOnlyMode()
    const savedNodeIds = useAppSelector((s) => s.savedNodes.savedNodeIds)
    const setMaskActions = useSetMaskActions()

    // Local State
    const [drawBrushCssPx, setDrawBrushCssPx] = useState<number>(24)
    const [drawErasing, setDrawErasing] = useState<boolean>(false)

    // Refs
    const containerRef = useRef<HTMLDivElement | null>(null)
    const drawHandleRef = useRef<DrawOverlayHandle | null>(null)
    const framingHookRef = useRef<ReturnType<typeof useFraming> | null>(null)
    const brushEnabledRef = useRef(false)
    const toolModeRef = useRef<'pointer' | 'grab' | 'select'>('pointer')
    const worldItemsRef = useRef<ReturnType<typeof useCanvasItems> | null>(null)

    // Core Hooks
    usePositionPersistence(session?.id ?? null, { enableServerSync: !readOnly })

    const { imageNaturalSize, imageSizesBySrc, handleImageLoad, handleVideoLoad, preloadMediaSizes } = useImageSizes()

    const { isDragOver, isUploading, handlers: dropHandlers } = useCanvasDrop(
        session,
        dispatch,
        readOnly
    )

    const {
        frameModeById,
        brushModeById,
        handleFrameModeToggle,
        handleBrushModeToggle,
        resetModes,
    } = useCanvasModes({
        selectedNodeIds: selected,
        sessionId: session?.id ?? null,
        frameEditState: framingHookRef.current?.frameEditState,
    })

    // Canvas Viewstate (pan/zoom, history, pointers)
    const {
        contentScale,
        setContentScale,
        contentOffset,
        setContentOffset,
        clientToWorld,
        onWheel,
        isPanning,
        spacePressed,
        history,
        withHistoryCapture,
        withInstantHistoryCapture,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        setPointerDelegates,
    } = useCanvasViewstate({
        containerRef,
        getBrushEnabled: () => brushEnabledRef.current,
        getToolMode: () => toolModeRef.current,
        onCanvasBackgroundClick: () => {
            if (!activeSessionId) return
            stateService.clearSelection(activeSessionId)
        },
        getSnapshotData: () => {
            const worldItemsSnapshot = worldItemsRef.current
            const worldRects = worldItemsSnapshot?.worldRectsById ?? {}
            const frameState = framingHookRef.current?.frameEditState ?? {}
            const initialWorldRects = worldItemsSnapshot?.initialWorldRectsById ?? {}
            return {
                worldRectsById: JSON.parse(JSON.stringify(worldRects)),
                frameEditState: JSON.parse(JSON.stringify(frameState)),
                initialWorldRectsById: JSON.parse(JSON.stringify(initialWorldRects)),
            }
        },
        applySnapshotData: (snap) => {
            const worldItemsSnapshot = worldItemsRef.current
            if (!worldItemsSnapshot) return
            worldItemsSnapshot.setWorldRects(snap.worldRectsById)
            if (snap.initialWorldRectsById) {
                worldItemsSnapshot.setInitialWorldRects(snap.initialWorldRectsById)
            }
            if (framingHookRef.current?.replaceFrameEditState) {
                framingHookRef.current.replaceFrameEditState(snap.frameEditState)
            }
        },
    })

    // Derived State
    const multiSelectionNodes = useMemo(() => {
        return canvasItemsState.map((it) => ({
            id: it.id,
            src: it.src,
            mediaType: it.mediaType,
        }))
    }, [canvasItemsState])

    // Brush Mode (must be before hooks that depend on it)
    const loraBrushModeRef = useRef<{
        exportFramedImageWithUpload?: () => Promise<{ node: any; commit: any } | null>
    }>({})

    const loraBrushMode = useLoraBrushMode({
        containerRef,
        contentScale,
        contentOffset,
        imageNaturalSize,
        imageRectInContainer: null,
        session: session ?? null,
        selected,
        globalBrushEnabled: globalBrushEnabled ?? false,
        lorasState: loras,
        setMaskActions,
        exportFramedImageWithUpload: () => loraBrushModeRef.current.exportFramedImageWithUpload?.() ?? Promise.resolve(null),
        setContentScale,
        setContentOffset,
    })

    const {
        brushEnabled,
        activeMaskItem,
        maskStageProps,
        brushToolbarProps,
        handleExitBrush,
    } = loraBrushMode
    const { imageRectInContainer, containerSize } = maskStageProps

    // Tool Mode (must be after brushEnabled is available)
    const { toolMode, setToolMode, resetToolMode } = useToolMode({
        brushEnabled,
        spacePressed,
    })

    const { previewSrc, previewMediaType, previewRect, captionText, captionRect } = usePromptedPreview({
        previewUrl: previewUrl ?? null,
        hoveredNodeId: hovered ?? null,
        session: session ?? null,
        containerSize,
        imageSizesBySrc,
    })

    // Image Resize Commit
    const handleImageResizeCommit = useImageResizeCommit({
        sessionId: session?.id ?? null,
        canvasItems: canvasItemsState,
        imageSizesBySrc,
        initialWorldRectsById: worldItemsRef.current?.initialWorldRectsById ?? {},
        dispatch,
    })

    // Canvas Items (world coordinates, positioning)
    const worldItems = useCanvasItems({
        containerRef,
        clientToWorld,
        imageRectInContainer,
        multiSelectionNodes,
        imageSizesBySrc,
        disableAutoRowReflow: true,
        suppressInitialPlacement: true,
        onImageRectChange: (id, rect) => {
            if (framingHookRef.current?.syncImageRect) {
                framingHookRef.current.syncImageRect(id, id, rect)
            }
        },
        sessionId: session?.id ?? null,
        dispatch,
        onImageResizeCommit: handleImageResizeCommit,
        selectedNodeIds: selected,
        toolMode,
    })
    worldItemsRef.current = worldItems
    const worldRects = worldItems.worldRectsList
    const drawTargetId = selected.find((id) => brushModeById[id]) ?? null
    const activeMaskMediaType = activeMaskItem?.id
        ? session?.nodes?.find((n) => n.id === activeMaskItem.id)?.type ?? 'image'
        : 'image'

    // Framing System
    const framingHook = useFraming({
        brushEnabled,
        worldRectsById: worldItems.worldRectsById,
        initialWorldRectsById: worldItems.initialWorldRectsById,
        setWorldRects: worldItems.setWorldRects,
        worldRects,
        imageRectInContainer: imageRectInContainer || null,
        imageNaturalSize: imageNaturalSize || null,
        imageSizesBySrc,
        selectedNodeIds: selected,
        sessionId: session?.id,
        dispatch,
        containerRef,
        clientToWorld,
        getDrawOverlayForId: async (id: string) => {
            if (id !== drawTargetId) return null
            const blob = await drawHandleRef.current?.exportOverlayBlob()
            return blob ?? null
        },
        hasDrawingChangesForId: (id: string) => {
            if (id !== drawTargetId) return false
            return drawHandleRef.current?.hasChanges() ?? false
        },
        frameModeById,
    })

    const {
        frameItems,
        resetFraming,
        commitLayoutPositions,
        onFrameSelect,
        onFrameMoveStart,
        onFrameResizeStart,
        onFramePointerMove,
        onFramePointerUp,
        onLayerMove,
        onLayerResize,
        reorderLayers,
        removeLayer,
    } = framingHook

    framingHookRef.current = framingHook

    // Composition System (Drag-to-Compose)
    const compositionHook = useCanvasInternalDrag({
        frameEditState: framingHook.frameEditState,
        worldRectsById: worldItems.worldRectsById,
        initialWorldRectsById: worldItems.initialWorldRectsById,
        imageSizesBySrc,
        addLayer: framingHook.addLayer,
        setWorldRects: worldItems.setWorldRects,
        removeNode: (nodeId: string) => {
            console.log('[Composition] Node added to frame as layer:', nodeId)
        },
        sessionId: session?.id ?? null,
        onDropComplete: (targetFrameId: string) => {
            // Select the composition target so layer controls appear
            if (session?.id) {
                dispatch(setSelectedNodes({ sessionId: session.id, nodeIds: [targetFrameId] }))
            }
            // Enable Frame Mode so the layer panel and crop overlay are visible
            if (!frameModeById[targetFrameId]) {
                handleFrameModeToggle(targetFrameId)
            }
        },
    })

    // Wrap world items handlers to track drag state for composition
    const worldItemsHandlersWithComposition = useMemo(() => {
        const originalHandlers = worldItems.layerHandlers
        return {
            onImageSelect: originalHandlers.onImageSelect,
            onImageMoveStart: (id: string, e: React.PointerEvent, src?: string) => {
                // Only set composition drag state for single-item drags (not group drag)
                const isGroupDrag = selected.includes(id) && selected.length > 1
                if (!isGroupDrag) {
                    const srcToUse = src || canvasItemsState.find(item => item.id === id)?.src || ''
                    const mediaTypeToUse = canvasItemsState.find(item => item.id === id)?.mediaType ?? 'image'
                    const worldPos = clientToWorld(e.clientX, e.clientY)
                    const startRect = worldItems.worldRectsById[id] || null
                    compositionHook.setDragState({
                        draggedNodeId: id,
                        draggedNodeSrc: srcToUse,
                        draggedNodeMediaType: mediaTypeToUse,
                        currentPosition: { x: e.clientX, y: e.clientY },
                        dragStartWorldPos: worldPos,
                        originalRect: startRect ? { ...startRect } : null,
                    })
                }
                originalHandlers.onImageMoveStart(id, e, src)
            },
            onImageResizeStart: originalHandlers.onImageResizeStart,
        }
    }, [worldItems.layerHandlers, compositionHook.setDragState, canvasItemsState, clientToWorld, selected])

    // Wrap pointer move handler to update ghost position
    const originalOnPointerMove = worldItems.onPointerMove
    const onPointerMoveWithComposition = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (compositionHook.dragState.hasCreatedCopy && 
            compositionHook.dragState.ghostRect && 
            compositionHook.dragState.dragStartWorldPos &&
            compositionHook.dragState.draggedNodeId) {
            
            const worldPos = clientToWorld(e.clientX, e.clientY)
            if (worldPos) {
                const dx = worldPos.x - compositionHook.dragState.dragStartWorldPos.x
                const dy = worldPos.y - compositionHook.dragState.dragStartWorldPos.y
                const originalRect = compositionHook.dragState.originalRect
                
                if (originalRect) {
                    compositionHook.setDragState({
                        ghostRect: {
                            x: originalRect.x + dx,
                            y: originalRect.y + dy,
                            w: originalRect.w,
                            h: originalRect.h,
                        }
                    })
                }
            }
            
            return true
        }
        
        const wasHandled = originalOnPointerMove(e)
        return wasHandled
    }, [originalOnPointerMove, compositionHook, clientToWorld])

    // Wrap pointer up handler to handle composition drop
    const originalOnPointerUp = worldItems.onPointerUp
    const onPointerUpWithComposition = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const wasHandled = originalOnPointerUp(e)
        
        if (compositionHook.dragState.draggedNodeId && compositionHook.dragState.targetFrameId) {
            compositionHook.setDragState({ ghostOpacity: 1.0 })
            requestAnimationFrame(() => {
                history.beginCapture()
                compositionHook.handleDrop()
                history.commitIfChanged()
            })
        } else if (compositionHook.dragState.draggedNodeId) {
            compositionHook.setDragState({
                draggedNodeId: null,
                draggedNodeSrc: null,
                draggedNodeMediaType: null,
                currentPosition: null,
                targetFrameId: null,
                hasCreatedCopy: false,
                originalRect: null,
                ghostRect: null,
                ghostOpacity: 0.7,
                dragStartWorldPos: null,
                returnAnimation: null,
            })
        }
        
        return wasHandled
    }, [originalOnPointerUp, compositionHook])

    // Export & Integration - Using new hook that handles upload and commit creation
    const exportFramedImageWithUpload = useExportFramedImageWithUpload({
        nodeId: activeMaskItem?.id ?? null,
        sourceUrl: activeMaskItem?.src ?? null,
        imageNaturalSize,
        frameEditStateRef: framingHook.frameEditStateRef,
        imageRectInContainer,
        initialWorldRectsById: worldItems.initialWorldRectsById,
        imageSizesBySrc,
        sessionId: session?.id ?? null,
        hasDrawingChanges: activeMaskItem?.id
            ? drawHandleRef.current?.hasChanges() ?? false
            : false,
        getDrawingOverlay: async () => {
            if (!activeMaskItem?.id || activeMaskItem.id !== drawTargetId) return null
            return (await drawHandleRef.current?.exportOverlayBlob()) ?? null
        },
    })

    const { hiddenIds, hideAll, showAll } = useHiddenItems(session?.id ?? null)
    useAutoAddCanvasItems({
        session,
        canvasItems: canvasItemsState,
        dispatch,
        hiddenIds,
        preloadMediaSizes,
    })

    const { ready: layoutReady, applyLayout } = useVerticalTreeLayout({
        session,
        imageSizesBySrc,
        canvasItems: canvasItemsState,
        containerRef: containerRef as RefObject<HTMLDivElement>,
        worldRectsById: worldItems.worldRectsById,
        setWorldRects: worldItems.setWorldRects,
        setContentScale,
        setContentOffset,
        currentScale: contentScale,
        currentOffset: contentOffset,
        centerOnAdd: true,
        sessionId: session?.id ?? null,
    })

    useFocusOnNode({
        containerRef: containerRef as RefObject<HTMLDivElement>,
        worldRectsById: worldItems.worldRectsById,
        contentScale,
        contentOffset,
        setContentScale,
        setContentOffset,
        sessionId: session?.id ?? null,
    })

    // Apply pending "initial selection" only after canvas is positioned.
    // This prevents a single node (typically the last node) from being treated specially
    // during session entry and avoids transient placement/persistence artifacts.
    useEffect(() => {
        if (!pendingInitialSelectionId) return
        if (!session?.id) return
        if (!layoutReady) return
        const existsOnCanvas = canvasItemsState.some(
            (it) => it.id === pendingInitialSelectionId
        )
        if (!existsOnCanvas) return
        if (!activeSessionId) return
        dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: [pendingInitialSelectionId] }))
        dispatch(clearPendingInitialSelection({ sessionId: activeSessionId }))
    }, [pendingInitialSelectionId, layoutReady, session?.id, canvasItemsState, dispatch, activeSessionId])

    // Check which nodes are saved when session loads
    useEffect(() => {
        if (!session?.nodes?.length) return
        const nodeIds = session.nodes.map((n: { id: string }) => n.id)
        dispatch(checkSavedNodes(nodeIds))
    }, [session?.id, session?.nodes?.length, dispatch])

    const handleSaveToggle = useCallback((nodeId: string) => {
        if (savedNodeIds[nodeId]) {
            dispatch(unsaveNode(nodeId))
        } else {
            dispatch(saveNode(nodeId))
        }
    }, [dispatch, savedNodeIds])

    // Context Menu
    const {
        open: menuOpen,
        anchor: menuAnchor,
        target: menuTarget,
        openWithEvent,
        close: closeMenu,
    } = useContextMenu(containerRef)

    const downloadUrl = useDownloadUrl()
    const menuItems = useContextMenuItems({
        target: menuTarget,
        downloadUrl,
    })

    // Canvas Coordinator
    const coordinator = useCanvasCoordinator(
        {
            framing: framingHook,
            brushEnabled,
            drawTargetId,
            brushToolbarProps,
            history,
        },
        {
            session,
            stateService,
            resetFraming,
            resetModes,
            showAll,
            hideAll,
            applyLayout,
            commitLayoutPositions,
            drawHandleRef,
            clearLocalSelection: worldItems.clearLocalSelection,
            canvasItemsState,
            resetToolMode,
        }
    )

    // Undo/Redo Shortcuts
    useUndoRedoShortcuts({
        enabled: true,
        onUndo: coordinator.handleUndo,
        onRedo: coordinator.handleRedo,
    })

    // Bulk Actions
    const bulkActions = useBulkActions({
        selectedNodeIds: selected,
        canvasItems: canvasItemsState,
        sessionId: session?.id ?? null,
        dispatch,
        savedNodeIds,
        hideAll,
        stateService,
        clearLocalSelection: worldItems.clearLocalSelection,
        resetFraming,
    })

    // Marquee Selection
    const marquee = useMarqueeSelection({
        containerRef,
        clientToWorld,
        worldRectsById: worldItems.worldRectsById,
        toolMode,
        sessionId: session?.id ?? null,
        dispatch,
        currentSelectedIds: selected,
    })

    // Canvas Handlers
    const canvasHandlers = useCanvasHandlers({
        worldItemsHandlers: worldItemsHandlersWithComposition,
        worldItemsState: {
            selectedWorldItemId: worldItems.selectedWorldItemId,
            clearLocalSelection: worldItems.clearLocalSelection,
        },
        frameModeById,
        onLayerMove,
        onLayerResize,
        onFrameSelect,
        onFrameMoveStart,
        onFrameResizeStart,
        onReorderLayers: reorderLayers,
        onRemoveLayer: removeLayer,
        handleImageLoad,
        handleVideoLoad,
        selectedNodeIds: selected,
        sessionId: session?.id ?? null,
        dispatch,
        toolMode,
        openContextMenu: openWithEvent,
        withHistoryCapture,
        withInstantHistoryCapture,
    })

    // Effects: Ref Syncing
    useEffect(() => {
        brushEnabledRef.current = brushEnabled
        toolModeRef.current = toolMode
        loraBrushModeRef.current.exportFramedImageWithUpload = exportFramedImageWithUpload
    }, [brushEnabled, toolMode, exportFramedImageWithUpload])

    // Effects: Pointer Delegates
    usePointerDelegates({
        setPointerDelegates,
        worldDelegates: {
            onPointerMove: onPointerMoveWithComposition,
            onPointerUp: onPointerUpWithComposition,
            clearLocalSelection: worldItems.clearLocalSelection,
        },
        frameDelegates: {
            onPointerMove: onFramePointerMove,
            onPointerUp: onFramePointerUp,
        },
        marqueeDelegates: {
            startMarquee: marquee.startMarquee,
            moveMarquee: marquee.moveMarquee,
            endMarquee: marquee.endMarquee,
        },
    })

    // Effects: Session & Mode Management
    useSessionCleanup({
        sessionId: session?.id ?? null,
        onSessionChange: () => {
            resetFraming()
            history.clear()
        },
    })

    useAutoAddSelectedNode({
        session,
        selectedNodeIds: selected,
        canvasItems: canvasItemsState,
        dispatch,
        isSessionLoading,
    })

    // Render
    const rootClassName = [
        styles.root,
        !brushEnabled && isPanning && styles.cursorPanning,
        !brushEnabled && !isPanning && spacePressed && styles.cursorPanReady,
        !brushEnabled && !isPanning && !spacePressed && toolMode === 'grab' && styles.cursorPanReady,
        !brushEnabled && !isPanning && !spacePressed && toolMode === 'select' && styles.cursorCrosshair,
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div
            className={rootClassName}
            ref={containerRef}
            onDrop={dropHandlers.onDrop}
            onDragOver={dropHandlers.onDragOver}
            onDragLeave={dropHandlers.onDragLeave}
            onWheel={onWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            {/* Overlays */}
            <DropOverlay isDragOver={isDragOver} isUploading={isUploading} />
            <MarqueeOverlay marqueeRect={marquee.marqueeRect} />

            <ContextMenu
                open={menuOpen}
                anchor={menuAnchor}
                onClose={closeMenu}
                items={menuItems}
            />

            {/* Toolbar */}
            <CanvasToolbar
                brushEnabled={brushEnabled}
                toolMode={toolMode}
                selectedCount={selected.length}
                readOnly={readOnly}
                canUndo={brushToolbarProps.canUndo}
                canRedo={brushToolbarProps.canRedo}
                brushSizeCssPx={brushToolbarProps.brushSizeCssPx}
                onBrushSizeChange={brushToolbarProps.onBrushSizeChange}
                onUndo={brushToolbarProps.onUndo}
                onRedo={brushToolbarProps.onRedo}
                onExitBrush={handleExitBrush}
                onToolModeChange={setToolMode}
                onViewportUndo={history.undo}
                onViewportRedo={history.redo}
                canViewportUndo={history.canUndo}
                canViewportRedo={history.canRedo}
                onAutoLayout={coordinator.handleAutoLayout}
                onDownload={bulkActions.handleBulkDownload}
                onSave={bulkActions.handleBulkSave}
                onRemove={bulkActions.handleBulkRemove}
                spacePressed={spacePressed}
            />

            {/* Konva Mask Stage (for brush drawing) */}
            <MaskStage {...maskStageProps} />

            {/* Brush Mode: Single active image */}
            {brushEnabled && (
                <CanvasViewport
                    className={styles.content}
                    contentScale={contentScale}
                    contentOffset={contentOffset}
                >
                    {activeMaskItem?.src && (
                        activeMaskMediaType === 'video' ? (
                            <VideoItem
                                src={activeMaskItem.src}
                                rect={imageRectInContainer || undefined}
                                onLoadedMetadata={handleVideoLoad(activeMaskItem.src)}
                                onError={() =>
                                    console.warn('Canvas video failed', activeMaskItem.src)
                                }
                            />
                        ) : (
                            <ImageItem
                                src={activeMaskItem.src}
                                rect={imageRectInContainer || undefined}
                                onLoad={handleImageLoad(activeMaskItem.src)}
                                onError={() =>
                                    console.warn('Canvas image failed', activeMaskItem.src)
                                }
                            />
                        )
                    )}
                </CanvasViewport>
            )}

            {/* Non-Brush Mode: Multi-selection canvas items */}
            {!brushEnabled && (
                <CanvasViewport
                    className={styles.content + ' ' + styles.contentAbove}
                    contentScale={contentScale}
                    contentOffset={contentOffset}
                >
                    {/* In grab mode, disable pointer events on items so clicks fall through for panning */}
                    <div style={toolMode === 'grab' ? { pointerEvents: 'none' } : undefined}>
                    <FramedImageList
                        items={frameItems.map((item) => ({
                            ...item,
                            isDragTarget: compositionHook.dragState.targetFrameId === item.id,
                        }))}
                        contentScale={contentScale}
                        getNaturalSizeForSrc={(src: string) => imageSizesBySrc[src] || null}
                        onActivateLayer={(frameId, layerId) => framingHook.setActiveLayer(frameId, layerId)}
                        frameModeById={frameModeById}
                        brushModeById={brushModeById}
                        onFrameModeToggle={handleFrameModeToggle}
                        onBrushModeToggle={handleBrushModeToggle}
                        drawBrushSizeCssPx={drawBrushCssPx}
                        onDrawBrushSizeChange={(v: number) => setDrawBrushCssPx(v)}
                        drawErasing={drawErasing}
                        onToggleEraser={() => setDrawErasing((p) => !p)}
                        getDrawHandleRef={(id: string) => (id === drawTargetId ? drawHandleRef : null)}
                        handlers={canvasHandlers}
                        readOnly={readOnly}
                        savedNodeIds={savedNodeIds}
                        onSaveToggle={readOnly ? undefined : handleSaveToggle}
                    />
                    </div>

                    {/* Return animation overlay - shows image bouncing back to original position */}
                    {compositionHook.dragState.returnAnimation?.isAnimating &&
                     compositionHook.dragState.returnAnimation.fromRect &&
                     compositionHook.dragState.returnAnimation.toRect &&
                     compositionHook.dragState.returnAnimation.startTime &&
                     compositionHook.dragState.draggedNodeSrc && (
                        <ReturningImageOverlay
                            src={compositionHook.dragState.draggedNodeSrc}
                            mediaType={compositionHook.dragState.draggedNodeMediaType ?? 'image'}
                            fromRect={compositionHook.dragState.returnAnimation.fromRect}
                            toRect={compositionHook.dragState.returnAnimation.toRect}
                            startTime={compositionHook.dragState.returnAnimation.startTime}
                            onComplete={compositionHook.clearReturnAnimation}
                        />
                    )}
                    
                    {/* Ghost image overlay during drag-to-compose */}
                    {compositionHook.dragState.hasCreatedCopy && 
                     compositionHook.dragState.ghostRect && 
                     compositionHook.dragState.draggedNodeSrc && (
                        <GhostImageOverlay
                            src={compositionHook.dragState.draggedNodeSrc}
                            mediaType={compositionHook.dragState.draggedNodeMediaType ?? 'image'}
                            rect={compositionHook.dragState.ghostRect}
                            opacity={compositionHook.dragState.ghostOpacity}
                        />
                    )}
                </CanvasViewport>
            )}

            {/* Preview Overlay (shows in both modes) */}
            <PreviewOverlay
                visible={!!previewSrc}
                previewSrc={previewSrc}
                previewMediaType={previewMediaType}
                previewRect={previewRect}
                captionText={captionText}
                captionRect={captionRect}
                onImageLoad={handleImageLoad(previewSrc ?? '')}
                onVideoLoad={handleVideoLoad(previewSrc ?? '')}
                onImageError={() => console.warn('Canvas preview image failed', previewSrc)}
            />
        </div>
    )
}

export default Canvas
